import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { NavLink, useNavigate } from 'react-router-dom';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { ConfirmDialog, DataTable, EmptyState, PageHeader, StatusChip, useNotify, type DataTableColumn, type StatusTone } from '@routes-plan/ui';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import PermissionButton from '../../../components/common/PermissionButton';
import { shouldRenderForbiddenAction } from '../../../utils/permission-ui';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { Office } from '../../../models/office';
import { Route } from '../../../models/route';
import { LoanImportLog } from '../../../models/loan-import-log';
import { useT, translate, currentLang } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { companyLocale } from '../../../utils/format/currency';
import { FilterBar, OfficeRouteFields } from '../components/ProcessFilters';
import { selectionColumn } from '../components/selectionColumn';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const formatDateTime = (value?: string) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString(companyLocale());
};

const formatDuration = (durationMs: number) => {
  const lang = currentLang();
  if (!Number.isFinite(durationMs) || durationMs <= 0) return translate(lang, 'loanImports.durationSeconds', { s: 0 });

  const seconds = Math.round(durationMs / 1000);
  if (seconds < 60) return translate(lang, 'loanImports.durationSeconds', { s: seconds });

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return translate(lang, 'loanImports.durationMinutes', { m: minutes, s: remainingSeconds });
};

const getStatusLabel = (status: string, warning: boolean) => {
  const lang = currentLang();
  if (warning) return translate(lang, 'loanImports.statusWarning');
  if (status === 'success') return translate(lang, 'loanImports.statusSuccess');
  if (status === 'error') return translate(lang, 'loanImports.statusError');
  return translate(lang, 'loanImports.statusRunning');
};

// Tono del estado (colores del tema, en claro y oscuro)
const statusTone: Record<string, StatusTone> = {
  success: 'success',
  warning: 'warning',
  error: 'danger',
  running: 'info',
};

const LoanImportList: React.FC<ShowAlerts> = ({ showLoading, hideLoading }) => {
  const t = useT();
  const notify = useNotify();
  const navigate = useNavigate();
  const showAlert = (message: string, severity: 'success' | 'info' | 'warning' | 'error' = 'info') => notify[severity](message);
  useEffect(() => {
    setPageTitle('menu.processes.imports');
  }, []);
  const [confirmDelete, setConfirmDelete] = useState<{ single?: number; count: number } | null>(null);
  const { canRead, canDelete, canDeleteSelected } = useResourcePermissions('/processes/imports');
  const showDeleteActions = shouldRenderForbiddenAction(canDelete);
  const showDeleteSelectedActions = shouldRenderForbiddenAction(canDeleteSelected);

  const [logs, setLogs] = useState<LoanImportLog[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [officeId, setOfficeId] = useState('');
  const [routeId, setRouteId] = useState('');
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);

  const loadLogs = async (officeFilter = officeId, routeFilter = routeId) => {
    if (!canRead) {
      setLogs([]);
      return;
    }
    showLoading();
    try {
      const { data } = await axios.get('imports', {
        params: {
          officeId: officeFilter || undefined,
          routeId: routeFilter || undefined,
          limit: 300,
        },
      });

      setLogs(data?.data ?? []);
    } catch (error: any) {
      console.error('Error cargando historial de importaciones:', error);
      showAlert(error?.response?.data?.message || t('loanImports.loadHistoryError'), 'error');
    } finally {
      hideLoading();
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      showLoading();
      try {
        const { data } = await axios.get('offices');
        setOffices(data?.data ?? []);
      } catch (error) {
        showAlert(t('loanImports.loadOfficesError'), 'error');
      } finally {
        hideLoading();
      }
    };

    bootstrap();
    if (canRead) {
      loadLogs('', '');
    }
  }, [canRead]);

  useEffect(() => {
    if (!officeId) {
      setRoutes([]);
      setRouteId('');
      return;
    }

    const fetchRoutes = async () => {
      showLoading();
      try {
        const { data } = await axios.get(`offices/${officeId}/routes`);
        setRoutes(data ?? []);
      } catch (error) {
        setRoutes([]);
        showAlert(t('loanImports.loadRoutesError'), 'warning');
      } finally {
        hideLoading();
      }
    };

    fetchRoutes();
  }, [officeId]);

  const handleSearch = async () => {
    if (!canRead) {
      showAlert(t('loanImports.noPermissionQueryImports'), 'warning');
      return;
    }
    await loadLogs();
  };

  const handleDeleteLog = async (id: number) => {
    if (!canDelete) return;
    showLoading();
    try {
      await axios.delete(`imports/${id}`);
      setLogs((prev) => prev.filter((item) => item.id !== id));
      setSelectedIds((prev) => prev.filter((item) => item !== id));
      showAlert(t('loanImports.deleteLogOk'), 'success');
    } catch (error: any) {
      showAlert(error?.response?.data?.message || t('loanImports.deleteLogError'), 'error');
    } finally {
      hideLoading();
    }
  };

  const handleDeleteSelected = async () => {
    if (!canDeleteSelected) return;
    if (selectedIds.length === 0) {
      showAlert(t('loanImports.selectAtLeastOne'), 'warning');
      return;
    }

    showLoading();
    try {
      await axios.post('imports/bulk-delete', {
        ids: selectedIds.map((id) => Number(id)),
      });

      const idsToDelete = new Set(selectedIds.map((id) => Number(id)));
      setLogs((prev) => prev.filter((item) => !idsToDelete.has(item.id)));
      setSelectedIds([]);
      showAlert(t('loanImports.deleteManyOk'), 'success');
    } catch (error: any) {
      showAlert(error?.response?.data?.message || t('loanImports.deleteManyError'), 'error');
    } finally {
      hideLoading();
    }
  };

  const rows = logs.map((log) => ({
    id: log.id,
    traceId: log.traceId,
    statusKey: log.warning ? 'warning' : log.status,
    statusLabel: getStatusLabel(log.status, Boolean(log.warning)),
    officeName: log.office?.name ?? '',
    routeName: log.route?.name ?? '',
    userName: log.user?.name ?? '',
    startedAtRaw: log.startedAt ?? '',
    startedAtLabel: formatDateTime(log.startedAt),
    finishedAtLabel: formatDateTime(log.finishedAt),
    durationLabel: formatDuration(Number(log.durationMs ?? 0)),
    rowsReceived: Number(log.rowsReceived ?? 0),
    preparedRows: Number(log.preparedRows ?? 0),
    savedRows: Number(log.savedRows ?? 0),
    validationErrorCount: Number(log.validationErrorCount ?? 0),
    postProcessErrorCount: Number(log.postProcessErrorCount ?? 0),
    message: log.errorMessage || log.message || '',
  }));

  type Row = (typeof rows)[number];
  const columns: DataTableColumn<Row>[] = [
    ...(showDeleteSelectedActions
      ? [
          selectionColumn<Row>({
            rows,
            getId: (r) => r.id,
            selected: selectedIds,
            onChange: setSelectedIds,
            selectAllLabel: t('loanImports.selectAll'),
            selectRowLabel: (r) => t('loanImports.selectRow', { id: r.traceId }),
            disabled: !canDeleteSelected,
          }),
        ]
      : []),
    { id: 'startedAt', header: t('loanImports.colStart'), value: (r) => r.startedAtRaw, render: (r) => r.startedAtLabel, sortable: true },
    {
      id: 'status',
      header: t('common.status'),
      value: (r) => r.statusLabel,
      render: (r) => <StatusChip label={r.statusLabel} tone={statusTone[r.statusKey] ?? 'info'} />,
      sortable: true,
    },
    { id: 'officeName', header: t('common.office'), value: (r) => r.officeName, sortable: true, hideBelow: 'md' },
    { id: 'routeName', header: t('common.route'), value: (r) => r.routeName, sortable: true },
    { id: 'userName', header: t('common.user'), value: (r) => r.userName, hideBelow: 'md' },
    { id: 'rowsReceived', header: t('loanImports.colRows'), value: (r) => r.rowsReceived, align: 'right', sortable: true },
    { id: 'savedRows', header: t('loanImports.colSaved'), value: (r) => r.savedRows, align: 'right', sortable: true },
    { id: 'preparedRows', header: t('loanImports.colPrepared'), value: (r) => r.preparedRows, align: 'right', hideBelow: 'md' },
    { id: 'validationErrorCount', header: t('loanImports.colValidationErrors'), value: (r) => r.validationErrorCount, align: 'right', hideBelow: 'sm' },
    { id: 'postProcessErrorCount', header: t('loanImports.colProcessErrors'), value: (r) => r.postProcessErrorCount, align: 'right', hideBelow: 'md' },
    { id: 'durationLabel', header: t('loanImports.colDuration'), value: (r) => r.durationLabel, hideBelow: 'md' },
    { id: 'finishedAt', header: t('loanImports.colEnd'), value: (r) => r.finishedAtLabel, hideBelow: 'md' },
    { id: 'traceId', header: t('loanImports.colTraceId'), value: (r) => r.traceId, hideBelow: 'md' },
    { id: 'message', header: t('loanImports.colMessage'), value: (r) => r.message, wrap: true },
  ];

  const runDelete = async () => {
    const target = confirmDelete;
    setConfirmDelete(null);
    if (!target) return;
    if (target.single !== undefined) await handleDeleteLog(target.single);
    else await handleDeleteSelected();
  };

  return (
    <HeaderResponsive>
      <PageHeader
        title={t('menu.processes.imports')}
        description={t('loanImports.historySubtitle')}
        breadcrumbs={[{ label: t('menu.processes.import'), onClick: () => navigate('/processes/import') }, { label: t('menu.processes.imports') }]}
        actions={
          <>
            {showDeleteSelectedActions && (
              <Tooltip title={canDeleteSelected ? '' : t('loanImports.noPermissionDeleteSelected')}>
                <span>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutlineOutlinedIcon />}
                    onClick={() => setConfirmDelete({ count: selectedIds.length })}
                    disabled={!canDeleteSelected || selectedIds.length === 0}
                  >
                    {selectedIds.length > 0 ? t('loanImports.deleteSelectedCount', { count: selectedIds.length }) : t('loanImports.deleteSelected')}
                  </Button>
                </span>
              </Tooltip>
            )}
            <Button variant="contained" startIcon={<UploadFileOutlinedIcon />} component={NavLink} to="/processes/import">
              {t('loanImports.goToImport')}
            </Button>
          </>
        }
      />

      <FilterBar>
        <OfficeRouteFields
          idPrefix="imports"
          allowAll
          offices={offices}
          routes={routes}
          officeId={officeId}
          routeId={routeId}
          onOfficeChange={setOfficeId}
          onRouteChange={setRouteId}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 56 }}>
          <PermissionButton
            allowed={canRead}
            deniedTooltip={t('loanImports.noPermissionQuery')}
            fullWidth
            size="large"
            variant="contained"
            startIcon={<HistoryOutlinedIcon />}
            onClick={handleSearch}
            spanSx={{ width: '100%' }}
          >
            {t('loanImports.query')}
          </PermissionButton>
        </Box>
      </FilterBar>

      <DataTable<Row>
        rows={rows}
        columns={columns}
        getRowId={(r) => r.id}
        searchable
        exportFileName={t('loanImports.historyExportFileName')}
        initialSort={{ columnId: 'startedAt', direction: 'desc' }}
        ariaLabel={t('menu.processes.imports')}
        rowActions={
          showDeleteActions
            ? (r) => (
                <Tooltip title={canDelete ? t('common.delete') : t('common.noPermissionDelete')}>
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={!canDelete}
                      aria-label={`${t('common.delete')} ${r.traceId}`}
                      onClick={() => setConfirmDelete({ single: Number(r.id), count: 1 })}
                    >
                      <DeleteOutlineOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )
            : undefined
        }
        emptyState={<EmptyState dense icon={<HistoryOutlinedIcon />} title={t('loanImports.emptyHistory')} />}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        variant="danger"
        title={t('loanImports.confirmDeleteTitle')}
        message={
          confirmDelete && confirmDelete.single === undefined
            ? t('loanImports.confirmDeleteMany', { count: confirmDelete.count })
            : t('loanImports.confirmDeleteLog')
        }
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void runDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
    </HeaderResponsive>
  );
};

export default LoanImportList;
